import { Box, MessageBlock } from '@deckee/deck-hand';
import { DateTime } from 'luxon';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { getReminders } from '../../../../services/reminders';
import { setSideBar } from '../../../../services/store/modules/app-controls';
import PageHolderContent from '../../../containers/PageHolderContent/PageHolderContent';
import HeaderBarButton from '../../../layouts/components/HeaderBarButton/HeaderBarButton';
import { back, plus } from '../../../shared/vector-images';
import HeaderBar from '../../../shared/_deprecated/features/HeaderBar/HeaderBar';
import AddReminderItem from '../components/AddReminderItem/AddReminderItem';
import ReminderItem from '../components/ReminderItem/ReminderItem';

const StyledReminderItem = styled(ReminderItem)`
  flex: 1 1 100%;
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

const StyledAddReminderItem = styled(AddReminderItem)`
  flex: 1 1 100%;
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

interface ReminderInterface {
  myRemindersItemType: string;
  label: string;
  description?: string;
}

interface Reminder extends ReminderInterface {
  id: number;
  expiryDate: string;
  expiryTimezone?: string;
}

interface ReminderDisplay extends Reminder {
  expired: 'valid' | 'soon' | 'expired';
  formattedDate: string;
}

const RemindersHomePage: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const userTimezone = DateTime.local().zoneName;

  const [isLoading, setIsLoading] = useState(false);

  const [reminderItems, setReminderItems] = useState<ReminderDisplay[]>([]);
  const [recommendedReminderItems, setRecommendedReminderItems] = useState<
    ReminderInterface[]
  >([]);

  const loadReminders = useCallback(async () => {
    setIsLoading(true);

    const remindersTypes = window.DECKEE_CONFIG?.reminders?.types
      ? JSON.parse(window.DECKEE_CONFIG.reminders.types)
      : null;

    // Get any reminders from the entity store
    const reminders = await getReminders();
    const remindersItems: Reminder[] = await reminders.reminders;

    // Add recommended reminders
    if (remindersItems && remindersTypes) {
      const whitelistedMyReminderTypes = window.DECKEE_CONFIG?.reminders
        ?.whitelistedTypes
        ? JSON.parse(window.DECKEE_CONFIG.reminders.whitelistedTypes)
        : [];

      const recommendedReminders: ReminderInterface[] = remindersTypes
        // Custom selection of high importance reminders to show to users
        .filter((reminder: ReminderInterface) =>
          whitelistedMyReminderTypes.includes(reminder.myRemindersItemType),
        )
        // Remove the items that the user already has reminders for
        // as they don't need to be prompted to create another reminder
        // for that item
        .filter(
          (reminder: ReminderInterface) =>
            !remindersItems.some(
              (items) =>
                items.myRemindersItemType === reminder.myRemindersItemType,
            ),
        )
        .map((reminder: ReminderInterface) => ({
          myRemindersItemType: reminder.myRemindersItemType,
          label: reminder.label,
        }));

      recommendedReminders.sort((a, b) =>
        a.myRemindersItemType > b.myRemindersItemType ? 1 : -1,
      );

      // Add custom recommended reminder
      recommendedReminders.push({
        myRemindersItemType: 'custom',
        label: 'Add your own',
      });
      setRecommendedReminderItems(recommendedReminders);
    }

    // Check if reminder is expired or due
    const isExpiredOrDue = (date: string) => {
      const nowMillis = DateTime.now().toMillis();
      const dateMillis = DateTime.fromISO(date).toMillis();
      const timeTilExpiry = dateMillis - nowMillis;

      switch (true) {
        case timeTilExpiry <= 0:
          return 'expired';
        // TODO: confirm soon time - set atm to 3 weeks
        case timeTilExpiry < 1000 * 60 * 60 * 24 * 21:
          return 'soon';
        default:
          return 'valid';
      }
    };
    // Format the date string
    const formatDate = (
      date: string,
      expiryTimezone: string | undefined,
      expired: string,
    ) => {
      let timeZoneLabel = '';
      if (userTimezone && expiryTimezone && userTimezone !== expiryTimezone) {
        timeZoneLabel = ` (${expiryTimezone})`;
      }

      const formattedDate = DateTime.fromISO(date).toFormat('d LLL y');
      let prefix = 'Due';
      if (expired === 'expired') prefix = 'Expired';
      if (expired === 'soon') prefix = 'Expires';

      return `${prefix} ${formattedDate}${timeZoneLabel}`;
    };

    const formatReminders = (rawReminders: Reminder[]) =>
      rawReminders.map((reminder) => {
        const expired = isExpiredOrDue(reminder.expiryDate);
        // create a new object to store new details
        const newReminder: ReminderDisplay = {
          ...reminder,
          expired,
          formattedDate: formatDate(
            reminder.expiryDate,
            reminder.expiryTimezone,
            expired,
          ),
        };
        return newReminder;
      });
    const formattedReminders: ReminderDisplay[] =
      formatReminders(remindersItems);

    // Sort reminders by reminder type
    formattedReminders.sort((a, b) => (a.expiryDate > b.expiryDate ? 1 : -1));

    // Add my reminders to state
    setReminderItems(formattedReminders);

    setIsLoading(false);
  }, [userTimezone]);

  const handleBack = async () => {
    await captureEvent({
      type: 'reminders: back to prepare',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps/prepare');
  };

  const handleSelectReminderType = async () => {
    await captureEvent({
      type: 'reminders: goto selkect reminder type',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps/reminders/new/select-type');
  };

  const handleCreateReminder = async (reminderType: string) => {
    await captureEvent({
      type: 'reminders: create new reminder',
      payload: { reminderType },
      sendToAmplitude: true,
    });
    history.push(`/maps/reminders/new/${reminderType}`);
  };

  const handleEditReminder = async (
    reminderType: string,
    reminderId: number,
  ) => {
    await captureEvent({
      type: 'reminders: create new reminder',
      payload: { reminderType, reminderId },
      sendToAmplitude: true,
    });
    history.push(`/maps/reminders/edit/${reminderType}/${reminderId}`);
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      dispatch(setSideBar());
      loadReminders();
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, loadReminders]);

  return (
    <PageHolderContent
      isLoading={isLoading}
      headerBarContent={
        <HeaderBar
          leftNode={
            <HeaderBarButton
              ariaLabel="return"
              icon={back()}
              onClick={handleBack}
            />
          }
          centerNode="Smart reminders"
          rightNode={
            <HeaderBarButton
              ariaLabel="add"
              icon={plus()}
              onClick={handleSelectReminderType}
            />
          }
        />
      }
    >
      <Box display="flex" flexWrap="wrap" boxSizing="border-box" p={2}>
        {!reminderItems.length && (
          <MessageBlock type="plain" size="medium" mb={3} p={3}>
            <Box mb={2} fontSize={4} fontWeight="bold">
              Always be prepared with smart reminders
            </Box>
            <Box mb={2} fontSize={2}>
              Never forget when your equipment expires or when your licenses or
              boat maintenance is due. Get started with these suggestions.
            </Box>
          </MessageBlock>
        )}
        <Box width="100%" mb={3}>
          {reminderItems &&
            reminderItems.map((item) => {
              const { id, myRemindersItemType, expired, formattedDate, label } =
                item;

              if (!label) return false;

              return (
                <StyledReminderItem
                  key={`${myRemindersItemType} - ${label}`}
                  description={formattedDate}
                  expired={expired}
                  label={label}
                  action={() => handleEditReminder(myRemindersItemType, id)}
                />
              );
            })}
        </Box>

        <Box mx={2} mb={2}>
          <Box fontSize={3} fontWeight="bold">
            Recommended reminders for you
          </Box>
          <Box mb={2} fontSize={2}>
            More reminders for better boating
          </Box>
        </Box>

        {recommendedReminderItems.map((item) => {
          const { myRemindersItemType, label } = item;

          if (!label) return false;

          return (
            <StyledAddReminderItem
              key={`${myRemindersItemType} - ${label}`}
              type="myRemindersItemType"
              label={label}
              action={() => handleCreateReminder(myRemindersItemType)}
            />
          );
        })}
      </Box>
    </PageHolderContent>
  );
};
export default RemindersHomePage;
